import {observable, action} from "mobx"

export type PendingMessageStatus = "sending" | "failed" | "unconfirmed"

export type PendingMessage = {
    clientMessageId: string
    roomId: number | string
    content: string
    payload: any
    status: PendingMessageStatus
    errorMessage?: string
}

// Time to wait for a newMessage echo before showing "not confirmed"
const CONFIRM_TIMEOUT = 15000

const items = observable.array<PendingMessage>([], {deep: false})
const timers: {[clientMessageId: string]: any} = {}

const find = (clientMessageId: string) => items.find((x) => x.clientMessageId === clientMessageId)

const replace = (clientMessageId: string, changes: Partial<PendingMessage>) => {
    const index = items.findIndex((x) => x.clientMessageId === clientMessageId)
    if (index >= 0) {
        items[index] = {...items[index], ...changes}
    }
}

const clearTimer = (clientMessageId: string) => {
    clearTimeout(timers[clientMessageId])
    delete timers[clientMessageId]
}

const startTimer = (clientMessageId: string) => {
    clearTimer(clientMessageId)
    timers[clientMessageId] = setTimeout(() => {
        const item = find(clientMessageId)
        if (item && item.status === "sending") {
            replace(clientMessageId, {status: "unconfirmed"})
        }
    }, CONFIRM_TIMEOUT)
}

const add = action((clientMessageId: string, roomId: number | string, content: string, payload: any) => {
    items.push({clientMessageId, roomId, content, payload, status: "sending"})
    startTimer(clientMessageId)
})

/** newMessage arrived with this clientMessageId: success always prevails. */
const resolve = action((clientMessageId: string) => {
    clearTimer(clientMessageId)
    const item = find(clientMessageId)
    if (item) items.remove(item)
})

const fail = action((clientMessageId: string, errorMessage?: string) => {
    if (!find(clientMessageId)) return
    clearTimer(clientMessageId)
    replace(clientMessageId, {status: "failed", errorMessage})
})

const retry = action((clientMessageId: string) => {
    replace(clientMessageId, {status: "sending", errorMessage: undefined})
    startTimer(clientMessageId)
})

const discard = action(resolve)

/** Plain-text preview of the message content (it may be rich text HTML or mention markup). */
const getPreview = (content: string) => {
    const withBreaks = (content || "").replace(/<\/(p|div|li|h[1-6])>|<br\s*\/?>/gi, " ")
    const doc = new DOMParser().parseFromString(withBreaks, "text/html")
    return (doc.body.textContent || "")
        .replace(/\[@([^:\]]+):[^\]]+\]/g, "@$1")
        .replace(/\s+/g, " ")
        .trim()
}

/** Drops everything (and its timers); used when the session/socket is torn down so drafts never reach another user. */
const clear = action(() => {
    Object.keys(timers).forEach(clearTimer)
    items.clear()
})

const PendingMessages = {items, add, resolve, fail, retry, discard, clear, getPreview}

export default PendingMessages
